import {
  Button,
  Calendar,
  DatePicker,
  message,
  Modal,
  Spin,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import customParseFormat from "dayjs/plugin/customParseFormat";
import apiClient from "../../actions/api";
import isEmpty from "../../validations/is-empty";
import openNotification from "../../components/Notifications/openNotification";
import getDate from "../../untils/getDate";
import getHourAndMinute from "../../untils/getHourAndMinute";
import useSpecialActions from "../../hooks/useSpecialActions";
import {
  BorderOutlined,
  CopyOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  LoadingOutlined,
  UsergroupAddOutlined,
  CalendarOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";

// import style
import "../../style/calls.scss";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const weekFormat = "YYYY-MM-DD";
const monthFormat = "YYYY-MM";

const colors = [
  "#e11d1d",
  "#764FE2",
  "#CCCCCC",
  "#BECC00",
  "#00C2CC",
  "#C600CC",
  "#009D10",
  "#E3683E",
  "#cd1717",
  "#ff9a03",
  "#c60074",
  "#744fdd",
];

const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const allStatus = [
  {
    color: "gold",
    status: "Waiting",
  },
  {
    color: "",
    status: "Recruiter Phone",
  },
  {
    color: "",
    status: "Recruiter Video",
  },
  {
    color: "rgb(190, 0, 161)",
    status: "HR Call",
  },
  {
    color: "rgb(204, 0, 0)",
    status: "Technical",
  },
  {
    color: "rgb(0, 194, 204)",
    status: "Behavioural",
  },
  {
    color: "rgb(0, 157, 16)",
    status: "Final",
  },
  {
    color: "red",
    status: "Failed",
  },
];

const desiredOrder = [
  "Recruiter Phone",
  "Recruiter Video",
  "HR Call",
  "Technical",
  "Behavioural",
  "Final",
];

const statusNames = {
  0: "Waiting",
  1: "Recruiter Phone",
  2: "Recruiter Video",
  3: "Technical",
  4: "Behavioural",
  5: "Final",
  6: "Failed",
  7: "HR Call",
};

const groupedStatusCounts = (counts = []) => {
  const grouped = counts.reduce((acc, item) => {
    const status = statusNames[item.status];
    const statusInfo = allStatus.find((entry) => entry.status === status);
    if (statusInfo) acc[status] = { num: item.count, status: statusInfo };
    return acc;
  }, {});

  return desiredOrder.reduce((acc, status) => {
    if (grouped[status]) acc[status] = grouped[status];
    return acc;
  }, {});
};

const CalendarPage = ({ title, scope = "personal" }) => {
  const canUseSpecialActions = useSpecialActions();
  // set title
  document.title = title;

  const user = useSelector((state) => state.auth.user);
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const isTeamCalendar = scope === "team";
  const callsEndpoint = isTeamCalendar
    ? "/bids/get-all-calls"
    : "/bids/get-my-calls";
  const countsEndpoint = isTeamCalendar
    ? "/bids/get-call-counts"
    : "/bids/get-my-call-counts";
  const [results, setResults] = useState([]);
  const [allUser, setAllUser] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalFlag, setModalFlag] = useState(false);
  const [modalItem, setModalItem] = useState({});
  const [meetingTotal, setMeetingTotal] = useState(0);
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(new Date()).tz(timezoneMode.timezoneMode).startOf("week"),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(new Date()).tz(timezoneMode.timezoneMode).endOf("week"),
  );
  const [weekMeetings, setWeekMeetings] = useState(0);
  const [weekGroupedData, setWeekGroupedData] = useState({});
  const [searchMonth, setSearchMonth] = useState(
    dayjs(new Date()).tz(timezoneMode.timezoneMode),
  );
  const [monthMeetings, setMonthMeetings] = useState(0);
  const [monthGroupedData, setMonthGroupedData] = useState({});
  const [monthGroupedCallData, setMonthGroupedCallData] = useState({});
  const [, setAllGroupedData] = useState({});
  const [weekGroupedCallData, setWeekGroupedCallData] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(
    dayjs(new Date()).tz(timezoneMode.timezoneMode),
  );

  const customWeekStartEndFormat = (value) =>
    `${dayjs(value)
      .tz(timezoneMode.timezoneMode)
      .startOf("week")
      .format(weekFormat)} ~ ${dayjs(value)
      .tz(timezoneMode.timezoneMode)
      .endOf("week")
      .format(weekFormat)}`;

  const customMonthStartEndFormat = (value) =>
    `${dayjs(value)
      .tz(timezoneMode.timezoneMode)
      .startOf("month")
      .format(monthFormat)}`;

  /* Legacy client-side filtering retained temporarily for reference while the
     API rollout is deployed.
  const getFilteredData = useCallback(async () => {
    const startDate = dayjs(searchStartDate)
      .tz(timezoneMode.timezoneMode)
      .startOf("day");
    const endDate = dayjs(searchEndDate)
      .tz(timezoneMode.timezoneMode)
      .endOf("day");

    const filteredJobs = results.filter((job) => {
      const jobStart = dayjs(job.start_date).tz(timezoneMode.timezoneMode);
      const jobEnd = dayjs(job.end_date).tz(timezoneMode.timezoneMode);

      // Make sure that jobStart and jobEnd are valid dayjs objects
      if (!jobStart.isValid() || !jobEnd.isValid()) {
        return false; // If the date is not valid, exclude the job
      }

      return (
        jobStart.isBetween(startDate, endDate, null, "[)") ||
        jobEnd.isBetween(startDate, endDate, null, "[)")
      );
    });

    const tempWeekGroupedData = filteredJobs.reduce((acc, job) => {
      const { bidder_id } = job;
      const tempUser = allUser.find((tuser) => tuser.id === bidder_id);
      if (tempUser) {
        if (!acc[bidder_id]) {
          acc[bidder_id] = { num: 0, name: tempUser.name };
        }
        acc[bidder_id].num += 1;
      }
      return acc;
    }, {});

    const tempAllGroupedData = filteredJobs.reduce((acc, job) => {
      const { status } = job;
      const tempStatus = allStatus.find(
        (statusOne) => statusOne.status === status
      );
      if (tempStatus) {
        if (!acc[status]) {
          acc[status] = { num: 0, status: tempStatus };
        }
        acc[status].num += 1;
      }
      return acc;
    }, {});

    const sortedData = desiredOrder.reduce((acc, key) => {
      if (tempAllGroupedData.hasOwnProperty(key)) {
        acc[key] = tempAllGroupedData[key];
      }
      return acc;
    }, {});

    setWeekMeetings(filteredJobs.length);
    setWeekGroupedData(tempWeekGroupedData);
    setWeekGroupedCallData(sortedData);
  }, [results, searchStartDate, searchEndDate, allUser]);

  // Period summaries now come from the aggregate API instead of filtering the
  // visible calendar rows in the browser.

  const getMonthFilteredData = useCallback(async () => {
    const startOfMonth = searchMonth.startOf("month").toDate();
    const endOfMonth = searchMonth.endOf("month").toDate();

    const filteredJobs = results.filter((job) => {
      const jobStartDate = new Date(job.start_date);

      return jobStartDate >= startOfMonth && jobStartDate <= endOfMonth;
    });

    const tempMonthGroupedData = filteredJobs.reduce((acc, job) => {
      const { bidder_id } = job;
      const tempUser = allUser.find((tuser) => tuser.id === bidder_id);
      if (tempUser) {
        if (!acc[bidder_id]) {
          acc[bidder_id] = { num: 0, name: tempUser.name };
        }
        acc[bidder_id].num += 1;
      }
      return acc;
    }, {});

    const tempAllGroupedData = filteredJobs.reduce((acc, job) => {
      const { status } = job;
      const tempStatus = allStatus.find(
        (statusOne) => statusOne.status === status
      );
      if (tempStatus) {
        if (!acc[status]) {
          acc[status] = { num: 0, status: tempStatus };
        }
        acc[status].num += 1;
      }
      return acc;
    }, {});

    const sortedData = desiredOrder.reduce((acc, key) => {
      if (tempAllGroupedData.hasOwnProperty(key)) {
        acc[key] = tempAllGroupedData[key];
      }
      return acc;
    }, {});

    setMonthMeetings(filteredJobs.length);
    setMonthGroupedData(tempMonthGroupedData);
    setMonthGroupedCallData(sortedData);
  }, [results, searchMonth, allUser]);

  // The selected summary month is independent from the visible calendar month.

  const getLegacyCallData = useCallback(async () => {
    setLoading(true);

    await apiClient
      .get("/users/scope-users")
      .then((users) => {
        if (isEmpty(users.data)) {
          setAllUser([]);
          setLoading(false);
        } else {
          const tempUserData = users.data.map((item, key) => {
            return {
              ...item,
              key: key + 1,
              textcolor: item.text_color,
              color: item.background_color
                ? item.background_color
                : colors[key],
            };
          });

          apiClient
            .get("/bids/get-my-calls")
            .then((res) => {
              if (isEmpty(res.data)) {
                setResults([]);
                setLoading(false);
              } else {
                const tempData = res.data
                  .map((item, key) => {
                    return {
                      key: key + 1,
                      id: item._id,
                      company_name: item.company_name,
                      created_at: getDate(item.date),
                      docx_filename: item.docx_filename,
                      pdf_filename: item.pdf_filename,
                      job_url: item.job_url,
                      job_desc: item.job_desc,
                      docx_url: item.docx_url,
                      pdf_url: item.pdf_url,
                      status:
                        item.status === 0
                          ? "Waiting"
                          : item.status === 1
                          ? "Recruiter Phone"
                          : item.status === 2
                          ? "Recruiter Video"
                          : item.status === 7
                          ? "HR Call"
                          : item.status === 3
                          ? "Technical"
                          : item.status === 4
                          ? "Behavioural"
                          : item.status === 5
                          ? "Final"
                          : "Failed",
                      caller: item?.caller?.name,
                      caller_email: item?.caller?.email,
                      caller_id: item?.caller?._id,
                      caller_avatar: item?.caller?.avatar,
                      meeting_link: item?.meeting_link,
                      client_address: item?.client_address,
                      start_date: item?.start_date,
                      end_date: item?.end_date,
                      bidder: item?.user.name,
                      bidder_id: item?.user._id,
                      bidder_email: item?.user.email,
                      bidder_avatar: item?.user.avatar,
                    };
                  })
                  .sort((a, b) => {
                    const dataA = new Date(a.start_date);
                    const dataB = new Date(b.start_date);
                    return dataB - dataA;
                  });
                setMeetingTotal(tempData.length);
                setResults(tempData);

                const tempAllGroupedData = tempData.reduce((acc, job) => {
                  const { status } = job;
                  const tempStatus = allStatus.find(
                    (statusOne) => statusOne.status === status
                  );
                  if (tempStatus) {
                    if (!acc[status]) {
                      acc[status] = { num: 0, status: tempStatus };
                    }
                    acc[status].num += 1;
                  }
                  return acc;
                }, {});

                const sortedData = desiredOrder.reduce((acc, key) => {
                  if (tempAllGroupedData.hasOwnProperty(key)) {
                    acc[key] = tempAllGroupedData[key];
                  }
                  return acc;
                }, {});

                setAllGroupedData(sortedData);

                setLoading(false);

                const bidderCount = tempData.reduce((acc, job) => {
                  const { bidder_id } = job;
                  if (!acc[bidder_id]) {
                    acc[bidder_id] = 0;
                  }
                  acc[bidder_id] += 1;
                  return acc;
                }, {});

                // Step 3: Map bidder_id to user name and prepare the final data format
                const groupedData = Object.keys(bidderCount).map(
                  (bidder_id) => {
                    const userone = tempUserData.find(
                      (tuser) => tuser._id === bidder_id
                    );
                    return {
                      id: bidder_id,
                      num: bidderCount[bidder_id],
                      name: userone ? userone.name : "Unknown",
                      color: userone ? userone.color : "green",
                      textcolor: userone ? userone.textcolor : "black",
                    };
                  }
                );

                setAllUser(groupedData);
              }
            })
            .catch((err) => {
              if (!isEmpty(err?.response?.data?.errors)) {
                err.response.data.errors.forEach((item) => {
                  openNotification("error", "Validation Error!", item.msg);
                });
              }
              setLoading(false);
            });
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  }, []);

  */
  const showRequestError = useCallback((err) => {
    if (!isEmpty(err?.response?.data?.errors)) {
      err.response.data.errors.forEach((item) => {
        openNotification("error", "Validation Error!", item.msg);
      });
    }
  }, []);

  const periodParams = useCallback(
    (start, end) => ({
      startDate: dayjs(start)
        .tz(timezoneMode.timezoneMode)
        .startOf("day")
        .toISOString(),
      endDate: dayjs(end)
        .tz(timezoneMode.timezoneMode)
        .endOf("day")
        .toISOString(),
    }),
    [timezoneMode.timezoneMode],
  );

  const applyPeriodCounts = useCallback((data, period) => {
    const groupedUsers = data.byUser.reduce((acc, item) => {
      acc[item.userId] = { num: item.count };
      return acc;
    }, {});
    const groupedStatuses = groupedStatusCounts(data.byStatus);

    if (period === "week") {
      setWeekMeetings(data.total);
      setWeekGroupedData(groupedUsers);
      setWeekGroupedCallData(groupedStatuses);
    } else {
      setMonthMeetings(data.total);
      setMonthGroupedData(groupedUsers);
      setMonthGroupedCallData(groupedStatuses);
    }
  }, []);

  const loadPeopleAndTotals = useCallback(async () => {
    const totals = await apiClient.get(countsEndpoint);
    if (isTeamCalendar) {
      const users = await apiClient.get("/users/scope-users");
      const countByUser = new Map(
        totals.data.byUser.map((item) => [String(item.userId), item.count]),
      );
      setAllUser(
        users.data
          .map((item, key) => ({
            id: item._id,
            num: countByUser.get(String(item._id)) || 0,
            name: item.name,
            color: item.background_color || colors[key % colors.length],
            textcolor: item.text_color || "black",
            text_color: item.text_color || "black",
          }))
          .filter((item) => item.num > 0),
      );
    } else {
      setAllUser([
        {
          id: user._id,
          num: totals.data.total,
          name: user.name,
          color: user.background_color || colors[0],
          textcolor: user.text_color || "black",
          text_color: user.text_color || "black",
        },
      ]);
    }
    setMeetingTotal(totals.data.total);
    setAllGroupedData(groupedStatusCounts(totals.data.byStatus));
  }, [countsEndpoint, isTeamCalendar, user]);

  const loadCalendarMonth = useCallback(async () => {
    const params = periodParams(
      calendarMonth.startOf("month"),
      calendarMonth.endOf("month"),
    );
    const res = await apiClient.get(callsEndpoint, { params });
    const data = res.data
      .map((item, key) => ({
        key: key + 1,
        id: item._id,
        company_name: item.company_name,
        created_at: getDate(item.date),
        docx_filename: item.docx_filename,
        pdf_filename: item.pdf_filename,
        job_url: item.job_url,
        job_desc: item.job_desc,
        docx_url: item.docx_url,
        pdf_url: item.pdf_url,
        status: statusNames[item.status] || "Failed",
        caller: item?.caller?.name,
        caller_email: item?.caller?.email,
        caller_id: item?.caller?._id,
        caller_avatar: item?.caller?.avatar,
        meeting_link: item?.meeting_link,
        client_address: item?.client_address,
        start_date: item?.start_date,
        end_date: item?.end_date,
        bidder: isTeamCalendar ? item?.user?.name : user.name,
        bidder_id: isTeamCalendar ? item?.user?._id : user._id,
        bidder_email: isTeamCalendar ? item?.user?.email : user.email,
        bidder_avatar: isTeamCalendar ? item?.user?.avatar : user.avatar,
      }))
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
    setResults(data);
  }, [calendarMonth, callsEndpoint, isTeamCalendar, periodParams, user]);

  const loadWeekCounts = useCallback(async () => {
    const res = await apiClient.get(countsEndpoint, {
      params: periodParams(searchStartDate, searchEndDate),
    });
    applyPeriodCounts(res.data, "week");
  }, [
    applyPeriodCounts,
    countsEndpoint,
    periodParams,
    searchEndDate,
    searchStartDate,
  ]);

  const loadMonthCounts = useCallback(async () => {
    const res = await apiClient.get(countsEndpoint, {
      params: periodParams(
        searchMonth.startOf("month"),
        searchMonth.endOf("month"),
      ),
    });
    applyPeriodCounts(res.data, "month");
  }, [applyPeriodCounts, countsEndpoint, periodParams, searchMonth]);

  useEffect(() => {
    loadPeopleAndTotals().catch(showRequestError);
  }, [user, loadPeopleAndTotals, showRequestError]);

  useEffect(() => {
    setLoading(true);
    loadCalendarMonth()
      .catch(showRequestError)
      .finally(() => setLoading(false));
  }, [loadCalendarMonth, showRequestError]);

  useEffect(() => {
    loadWeekCounts().catch(showRequestError);
  }, [loadWeekCounts, showRequestError]);

  useEffect(() => {
    loadMonthCounts().catch(showRequestError);
  }, [loadMonthCounts, showRequestError]);

  const getListData = (value) => {
    let listData = []; // Specify the type of listData

    const currentDate = value
      .tz(timezoneMode.timezoneMode)
      .format("YYYY-MM-DD");

    listData = results
      .filter((item) => {
        const startDate = getDate(item.start_date);
        return startDate === currentDate;
      })
      .map((item) => {
        const oneuser = allUser.find(
          (oneUser) => oneUser.id === item.bidder_id,
        );

        return {
          ...item,
          text_color: oneuser ? oneuser.text_color : "#000",
          color: oneuser ? oneuser.color : "rgb(0, 157, 16)",
          content: `${getHourAndMinute(item.start_date)} <span style="color: ${
            item.status === "Recruiter Phone"
              ? ""
              : item.status === "Recruiter Video"
                ? ""
                : item.status === "HR Call"
                  ? "rgb(190, 0, 161)"
                  : item.status === "Technical"
                    ? "rgb(204, 0, 0)"
                    : item.status === "Behavioural"
                      ? "rgb(0, 194, 204)"
                      : item.status === "Final"
                        ? "rgb(0, 157, 16)"
                        : ""
          }; padding: 0 0 0 3px; font-weight: bold;">${item.status}</span>: ${
            item.caller
          }`,
        };
      })
      .sort((a, b) => {
        const dataA = new Date(a.start_date);
        const dataB = new Date(b.start_date);
        return dataA - dataB;
      });

    return listData || [];
  };

  const dateCellRender = (value) => {
    const listData = getListData(value);

    const countMap = listData.reduce((acc, { bidder_id }) => {
      acc[bidder_id] = (acc[bidder_id] || 0) + 1;
      return acc;
    }, {});

    // Step 2: Combine user data with the count of appearances
    const userCountsData = allUser.map((user) => ({
      ...user,
      count: countMap[user.id] || 0, // Default to 0 if no count found for the user
    }));

    return (
      <ul className="events-calendar">
        {listData.length > 0 && (
          <Tooltip
            placement="left"
            title={
              <div className="tool-tip-events">
                <div className="tool-tip-events-date">
                  <div className="tool-tip-events-day">
                    {
                      days[
                        dayjs(value).tz(timezoneMode.timezoneMode, true).day()
                      ]
                    }
                    ,{" "}
                    {
                      months[
                        dayjs(value).tz(timezoneMode.timezoneMode, true).month()
                      ]
                    }{" "}
                    {dayjs(value).tz(timezoneMode.timezoneMode, true).date()}
                  </div>
                </div>
                <div className="tool-tip-num-view">
                  {userCountsData.map((user, key) => (
                    <div key={key} className="tool-tip-num">
                      <span
                        className="tool-tip-num-circle"
                        style={{ backgroundColor: user.color }}
                      ></span>
                      {user.count}
                    </div>
                  ))}
                </div>
                <ul className="tool-tip-events-calendar">
                  {listData.map((item, key) => (
                    <li key={key}>
                      <span
                        className="tool-tip-events-badge-status"
                        onClick={() => handleOpenModal(item)}
                      >
                        <span
                          className="tool-tip-events-circle"
                          style={{ backgroundColor: item.color }}
                        ></span>
                        <span
                          className="tool-tip-events-text"
                          dangerouslySetInnerHTML={{ __html: item.content }}
                        ></span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            }
          >
            <span
              className="events-num"
              style={{
                backgroundColor: themeMode.themeMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.03)",
              }}
            >
              {listData.length}
            </span>
          </Tooltip>
        )}

        {listData.map((item, key) => (
          <li key={key}>
            <span
              className={`events-badge-status ${
                themeMode.themeMode ? "" : "events-badge-status-light"
              }`}
              onClick={() => handleOpenModal(item)}
            >
              <span
                className="events-circle"
                style={{ backgroundColor: item.color }}
              ></span>
              <span
                className="events-text"
                dangerouslySetInnerHTML={{ __html: item.content }}
              ></span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const handleDownloadResume = (docx_url) => {
    const link = document.createElement("a");
    link.href = `${API_BASE_URL}/job/description/${docx_url}`;
    link.setAttribute("download", "resume.docx");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownloadPDF = async (pdf_url, filename) => {
    const response = await fetch(`${API_BASE_URL}/job/description/${pdf_url}`, {
      method: "GET",
    });

    if (!response.ok) {
      // handle error
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadJD = async (content, companyname) => {
    const params = {
      content,
    };

    fetch(API_BASE_URL + "/api/bids/download/jd", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${companyname}.txt`; // desired filename
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
  };

  const cellRender = (current) => {
    return dateCellRender(current);
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success("Copied to clipboard!");
      } else {
        console.warn("Fallback: Copy command was unsuccessful");
        message.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      message.error("Failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = (copyText) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          message.success("Copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy!");
        });
    } else {
      fallbackCopyTextToClipboard(copyText);
    }
  };

  const handleOpenModal = (item) => {
    setModalFlag(true);
    setModalItem(item);
  };

  const handleModalCancel = () => {
    setModalFlag(false);
  };

  const getFormattedDate = (startDate, endDate) => {
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    // Get the day of the week, month, and date for the start date
    const weekday = start.format("dddd");
    const monthDay = start.format("MMMM D");

    // Get the time in 12-hour format with AM/PM
    const startTime = start.format("hh:mm A");
    const endTime = end.format("hh:mm A");

    return `${weekday}, ${monthDay} ⋅ ${startTime} - ${endTime}`;
  };

  const generateRandomColor = () => {
    const randomColor = Math.floor(Math.random() * 16777215).toString(16);
    return randomColor;
  };

  return (
    <div
      className={`callsSection calendar-page ${
        themeMode.themeMode ? "calendar-page--dark" : "calendar-page--light"
      }`}
    >
      <header className="calendar-page-header">
        <div className="calendar-page-heading-icon">
          <CalendarOutlined />
        </div>
        <div>
          <h2 className="title">
            {isTeamCalendar ? "Team Calendar" : "Calendar"}
          </h2>
          <p>
            {isTeamCalendar
              ? "Track team interviews, workload, and upcoming schedules."
              : "Track your interviews and upcoming schedule."}
          </p>
        </div>
      </header>
      <div className="call-calendar-view">
        {isTeamCalendar && (
          <div className="call-calendar-bidder-list calendar-summary-card">
            <div className="calendar-card-label">Team overview</div>
            {allUser.map((item) => (
              <div className="bidder-item" key={item.id}>
                <span
                  className="circle-item"
                  style={{ backgroundColor: item.color }}
                />
                <span style={{ color: item.color }}>
                  {item.name} ({item.num})
                </span>
              </div>
            ))}
            <div className="bidder-item">
              <div className="total-div-view">
                Total: {meetingTotal} Interviews
              </div>
            </div>
          </div>
        )}
        <div className="call-week-search-view calendar-filter-card">
          <div className="calendar-card-label">Period summary</div>
          <p className="week-total">
            {isTeamCalendar ? "Team has" : "You have"}{" "}
            <Tooltip
              placement="bottom"
              title={
                <>
                  {Object.keys(weekGroupedData).map((bidder_id) => {
                    const userone = allUser.find(
                      (tuser) => tuser.id === bidder_id,
                    );
                    return (
                      <p key={bidder_id}>
                        <span
                          style={{ color: userone ? userone.color : "green" }}
                        >
                          {userone ? userone.name : "Unknown"}:
                        </span>{" "}
                        {weekGroupedData[bidder_id].num}
                      </p>
                    );
                  })}
                  <hr className="hr-line" />
                  {Object.keys(weekGroupedCallData).map((status) => {
                    const userone = allStatus.find(
                      (statusOne) => statusOne.status === status,
                    );
                    return (
                      <p key={status.status}>
                        <span style={{ color: userone ? userone.color : "" }}>
                          {userone ? userone.status : "Unknown"}:
                        </span>{" "}
                        {weekGroupedCallData[status].num}
                      </p>
                    );
                  })}
                </>
              }
            >
              <span className="total-text">
                {weekMeetings > 0 ? weekMeetings : "No"}
              </span>
            </Tooltip>{" "}
            Interviews in{" "}
          </p>
          <DatePicker
            onChange={(date, dateString) => {
              if (isEmpty(dateString)) {
                setSearchStartDate(
                  dayjs(new Date())
                    .tz(timezoneMode.timezoneMode)
                    .startOf("week"),
                );
                setSearchEndDate(
                  dayjs(new Date()).tz(timezoneMode.timezoneMode).endOf("week"),
                );
              } else {
                setSearchStartDate(
                  dayjs(date).tz(timezoneMode.timezoneMode).startOf("week"),
                );
                setSearchEndDate(
                  dayjs(date).tz(timezoneMode.timezoneMode).endOf("week"),
                );
              }
            }}
            value={dayjs(searchStartDate).tz(timezoneMode.timezoneMode)}
            format={customWeekStartEndFormat}
            picker="week"
          />
          <p className="week-total">
            and{" "}
            <Tooltip
              placement="bottom"
              title={
                <>
                  {Object.keys(monthGroupedData).map((bidder_id) => {
                    const userone = allUser.find(
                      (tuser) => tuser.id === bidder_id,
                    );
                    return (
                      <p key={bidder_id}>
                        <span
                          style={{ color: userone ? userone.color : "green" }}
                        >
                          {userone ? userone.name : "Unknown"}:
                        </span>{" "}
                        {monthGroupedData[bidder_id].num}
                      </p>
                    );
                  })}
                  <hr className="hr-line" />
                  {Object.keys(monthGroupedCallData).map((status) => {
                    const userone = allStatus.find(
                      (statusOne) => statusOne.status === status,
                    );
                    return (
                      <p key={status.status}>
                        <span style={{ color: userone ? userone.color : "" }}>
                          {userone ? userone.status : "Unknown"}:
                        </span>{" "}
                        {monthGroupedCallData[status].num}
                      </p>
                    );
                  })}
                </>
              }
            >
              <span className="total-text">
                {monthMeetings > 0 ? monthMeetings : "No"}
              </span>
            </Tooltip>{" "}
            Interviews in{" "}
          </p>
          <DatePicker
            onChange={(date, dateString) => {
              if (isEmpty(dateString)) {
                setSearchMonth(dayjs(new Date()).tz(timezoneMode.timezoneMode));
              } else {
                setSearchMonth(dayjs(date).tz(timezoneMode.timezoneMode));
              }
            }}
            value={dayjs(searchMonth).tz(timezoneMode.timezoneMode)}
            format={customMonthStartEndFormat}
            picker="month"
          />
        </div>
        <div className="calendar-surface">
          <Calendar
            defaultValue={dayjs().tz(timezoneMode.timezoneMode)}
            cellRender={cellRender}
            locale="ja"
            onPanelChange={(date, mode) => {
              if (mode === "month") {
                setCalendarMonth(dayjs(date).tz(timezoneMode.timezoneMode));
              }
            }}
          />
        </div>

        {loading ? (
          <div className="calendar-loading">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
          </div>
        ) : (
          <></>
        )}

        <Modal
          title=""
          open={modalFlag}
          footer={[]}
          size={"sm"}
          width={430}
          onCancel={handleModalCancel}
        >
          <div className="scheduled-modal">
            <div className="scheduled-title">
              <div
                className="title-circle"
                style={{ backgroundColor: modalItem.color }}
              ></div>
              <div className="interview-title">
                {modalItem.company_name} -{" "}
                <span
                  style={{
                    fontWeight: "bold",
                    color:
                      modalItem.status === "Recruiter Phone"
                        ? ""
                        : modalItem.status === "Recruiter Video"
                          ? ""
                          : modalItem.status === "HR Call"
                            ? "rgb(190, 0, 161)"
                            : modalItem.status === "Technical"
                              ? "rgb(204, 0, 0)"
                              : modalItem.status === "Behavioural"
                                ? "rgb(0, 194, 204)"
                                : modalItem.status === "Final"
                                  ? "rgb(0, 157, 16)"
                                  : "",
                  }}
                >
                  {modalItem.status}
                </span>{" "}
                ({modalItem.caller})
              </div>
              <div className="schedule-date">
                {getFormattedDate(modalItem.start_date, modalItem.end_date)}
              </div>
            </div>
            <div className="meeting-link">
              <div className="meeting-icon">
                <EnvironmentOutlined />
              </div>
              <Tooltip placement="top" title={modalItem.meeting_link}>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => handleCopy(modalItem.meeting_link)}
                >
                  Join with Meeting
                </Button>
              </Tooltip>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(modalItem.meeting_link)}
              />
            </div>
            <div className="interviewers-section">
              <div className="interviewers-icon">
                <UsergroupAddOutlined />
              </div>
              <div className="interviewers">
                <img
                  alt="caller avatar"
                  className="interview-avatar"
                  src={modalItem.caller_avatar}
                />
                <p className="interviewer-address">{modalItem.caller_email}</p>
              </div>
              <div className="interviewers">
                <img
                  alt="client avatar"
                  className="interview-avatar"
                  src={`https://ui-avatars.com/api/?name=${
                    modalItem.client_address
                  }&background=${generateRandomColor()}&color=fff`}
                />
                <p className="interviewer-address">
                  {modalItem.client_address}
                </p>
              </div>
            </div>
            <div className="schedule-main-section">
              <div className="schedule-main-icon">
                <BorderOutlined />
              </div>

              <Tooltip placement="top" title="Download Resume as Docx">
                {!isEmpty(modalItem.docx_filename) ? (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadResume(modalItem.docx_url)}
                  >
                    {modalItem.docx_filename}
                  </Button>
                ) : (
                  <Button disabled color="danger" variant="dashed">
                    {modalItem.company_name}
                  </Button>
                )}
              </Tooltip>

              <Tooltip placement="top" title="Download Resume as PDF">
                {!isEmpty(modalItem.pdf_filename) ? (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      handleDownloadPDF(
                        modalItem.pdf_url,
                        modalItem.pdf_filename,
                      )
                    }
                  >
                    {modalItem.pdf_filename}
                  </Button>
                ) : (
                  <Button
                    disabled
                    icon={<DownloadOutlined />}
                    color="danger"
                    variant="dashed"
                  >
                    {modalItem.company_name}
                  </Button>
                )}
              </Tooltip>

              <Tooltip placement="top" title="Download JD">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownloadJD(modalItem.job_desc, modalItem.company_name)
                  }
                >
                  {modalItem.company_name} (JD)
                </Button>
              </Tooltip>
              <Tooltip placement="top" title={modalItem.job_url}>
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(modalItem.job_url)}
                >
                  Job URL
                </Button>
              </Tooltip>
              <div style={{ display: "flex", alignItems: "center" }}>
                Bidder:{" "}
                <img
                  alt="bidder avatar"
                  style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "50%",
                    marginRight: 5,
                    marginLeft: 5,
                  }}
                  src={modalItem.bidder_avatar}
                />
                <span style={{ color: modalItem.color }}>
                  {modalItem.bidder}
                </span>
              </div>
            </div>
            {canUseSpecialActions && (
              <div className="interview-link">
                <div className="interview-icon">
                  <PlayCircleOutlined />
                </div>
                <Tooltip
                  placement="top"
                  title="You can start interview with AI"
                >
                  <Button
                    color="pink"
                    variant="solid"
                    onClick={() => {
                      const url = `/interview/${modalItem.id}`;
                      window.open(url, "_blank");
                    }}
                  >
                    Start Interview with AI
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default CalendarPage;
